import { IHeadCell, MakeOptional, TableContextValue } from "lib/table"
import { Context, createContext, useContext } from "react"
import * as sort from "enums/sort"
import * as opt from "lib/opt"

const createTableCtx = <
	T extends Record<string, any>,
	K extends keyof T,
	U = unknown,
	S extends string | undefined = string,
>(
	value: MakeOptional<TableContextValue<T, K, U, S>, "order">
): Context<TableContextValue<T, K, U, S>> => {
	try {
		const contextValue: TableContextValue<T, K, U, S> = {
			order: value.order ?? sort.Asc("id"),
			headCells: value.headCells || [],
			params: value.params,
		}

		return createContext<TableContextValue<T, K, U, S>>(contextValue)
	} catch {
		// If SortEnum is not available yet, create context with minimal interface
		return createContext<TableContextValue<T, K, U, S>>({} as TableContextValue<T, K, U, S>)
	}
}

// Context Class
class tableCtx<
	TBase extends Record<string, any>,
	KBase extends keyof TBase = keyof TBase,
	UBase = unknown,
	SBase extends string | undefined = string,
> {
	private _ctx: Opt<Context<TableContextValue<TBase, KBase, UBase, SBase>>> = opt.None()

	Ctx<
		T extends Record<string, any>,
		K extends keyof T,
		U = unknown,
		S extends string | undefined = string,
	>(): Context<TableContextValue<T, K, U, S>> {
		return this._ctx.UnwrapOrElse(() => {
			this._ctx = opt.Some(
				createTableCtx<TBase, KBase, UBase, SBase>({
					headCells: [],
				})
			)
			return this._ctx.Unwrap()
		}) as unknown as Context<TableContextValue<T, K, U, S>>
	}

	Provider<
		T extends Record<string, any> = TBase,
		K extends keyof T = KBase & keyof T,
		U = UBase,
		S extends string | undefined = SBase,
	>() {
		return this.Ctx<T, K, U, S>().Provider
	}
}

// Context Hook
export const UseTableCtx = <
	T extends Record<string, any>,
	K extends keyof T = keyof T,
	U = unknown,
	S extends string | undefined = string,
>(): TableContextValue<T, K, U, S> => {
	const context = useContext(TableCtx.Ctx<T, K, U, S>())
	if (!context?.order) {
		throw new Error(
			"useTableContext must be used within a TableContextProvider with a valid order"
		)
	}
	return context
}

// Context Instance
export const TableCtx = new tableCtx()

export function CreateHeadCells<
	T extends Record<string, any>,
	U = unknown,
	K extends keyof T = keyof T,
	S extends string | undefined = string,
>(
	cells: {
		[P in K]: IHeadCell<T, P, U, S>
	}[K][],
	sortFn?: (property: K | S, index: int) => void
): HeadCell<T, K, U, S>[] {
	return cells.Map((cell) => {
		const out = HeadCell.From(cell)
		if (sortFn) {
			out.SetSort(sortFn)
		}
		return out
	})
}

export class HeadCell<
	T extends Record<string, any> | React.JSX.Element,
	K extends keyof T,
	U = unknown,
	S extends string | undefined = string,
> implements IHeadCell<T, K, U, S>
{
	readonly id: K
	readonly sortKey?: S
	readonly label: string | React.JSX.Element
	readonly numeric: bool = false
	readonly sortable: bool = true
	readonly width?: int
	readonly index?: int
	readonly mapper?: (value: T[K]) => string
	readonly renderCell?: (row: T, params?: U) => React.ReactNode
	private _onSort?: (property: K, index: int) => void

	constructor(readonly headCell: IHeadCell<T, K, U, S>) {
		this.id = headCell.id
		this.sortKey = headCell.sortKey
		this.label = headCell.label
		this.numeric = headCell.numeric ?? false
		this.sortable = headCell.sortable ?? true
		this.width = headCell.width
		this.mapper = headCell.mapper
		this.renderCell = headCell.renderCell
		this.index = headCell.index
		this.Sort = this.Sort.bind(this)
	}

	IsSortable(): bool {
		return isNil(this.sortable) || this.sortable
	}

	// Change setSort to use K type
	SetSort(handler: typeof this._onSort): void {
		this._onSort = handler
	}

	Sort(): void {
		if (this.IsSortable() && this._onSort) {
			// if sort key is provided use that instead of id
			this._onSort((this.sortKey ?? this.id) as K, this.index || 0)
		}
	}

	static From<
		T extends Record<string, any>,
		K extends keyof T,
		U = unknown,
		S extends string | undefined = string,
	>(input: IHeadCell<T, K, U, S>): HeadCell<T, K, U, S> {
		return new HeadCell<T, K, U, S>(input)
	}
}
